








































import {
    computed,
    defineComponent,
    PropType,
    ref,
    Ref,
    watch,
} from '@vue/composition-api';
import format from 'date-fns/format';
import { cloneDeep } from 'lodash-es';
import { DimensionFormatMap } from '../../const';
import { formatCountString, getReportSeries } from '../../service';
import {
    GroupSearchOption,
    LinkageParamType,
    TableDataLinkageSourceType,
    TableDataLinkageType,
    TableHeader,
    TableType,
} from '../../type';

export default defineComponent({
    props: {
        data: {
            type: Object as PropType<TableType>,
        },
        linkage: {
            type: Object as PropType<LinkageParamType>,
            required: true,
        },
        linkageOption: {
            type: Array as () => Array<GroupSearchOption>,
            required: true,
        },
        moduleName: {
            type: String,
            required: true,
        },
        appId: {
            type: String,
            required: true,
        },
    },
    setup(props, context) {
        const seperator = '|';
        const dateKey = 'dimension';
        const isLoading = ref(false);
        const tableData: Ref<Array<Record<string, any>>> = ref([]);
        const percentSourceSeries: Ref<Array<string>> = ref([]);
        const tableHeader = computed(() => {
            const copy = props.data?.headers.slice();
            if (!copy || copy.length === 0) return [];

            const prefixHeader = props.linkageOption.map((linkage) => {
                return {
                    seriesName:
                        linkage.searchType === 'date'
                            ? dateKey
                            : linkage.searchName,
                    name:
                        linkage.searchType === 'date'
                            ? '日期'
                            : linkage.searchCaption,
                    order: -1,
                } as TableHeader;
            });
            copy.sort((a, b) => a.order - b.order);
            return [...prefixHeader, ...copy];
        });
        let originData: Record<string, any> = {};
        let originTableData: Array<Record<string, any>> = [];

        const dimensionFormatType = DimensionFormatMap.day.type;
        const dimensionType = DimensionFormatMap.day.dimensionType;

        function downloadTableData(
            data: string,
            filename: string,
            type = 'text/csv',
        ) {
            const file = new Blob(['\ufeff' + data], { type: type });
            if ((window.navigator as any).msSaveOrOpenBlob)
                // IE10+
                (window.navigator as any).msSaveOrOpenBlob(file, filename);
            else {
                // Others
                const a = document.createElement('a');
                const url = URL.createObjectURL(file);
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                setTimeout(function () {
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                }, 0);
            }
        }

        function getLinkageSource(name: string) {
            const values = name.split('|');
            const ret = {} as TableDataLinkageSourceType;
            ret.sourceSeriesName = values.slice(0, 2).join('|');
            ret.linkageName = values.slice(2, values.length).join('|');
            return ret;
        }

        function getLinkageProperty(name: string) {
            const values = name.split('|');
            const mapper = [
                'product',
                'platform',
                'user_type',
                'from_name',
                'ad_type',
            ];
            const linkageProperty = {} as Record<string, any>;
            for (
                let i = 0;
                i < values.length && values.length === mapper.length;
                i++
            ) {
                linkageProperty[mapper[i]] = values[i];
            }
            const ret = linkageProperty as TableDataLinkageType;
            return ret;
        }

        function exportTableData() {
            if (
                !tableHeader.value ||
                Object.keys(originTableData).length === 0
            ) {
                return;
            }
            let exportData = tableHeader.value
                .map((header) => header.name)
                .join(',')
                .concat('\r\n');
            exportData += originTableData
                .map((row) =>
                    tableHeader.value
                        .map((header) => {
                            const data = row[header.seriesName] ?? 0;
                            if (header.seriesName === dateKey) {
                                return format(+data, dimensionFormatType);
                            }
                            return data;
                        })
                        .join(','),
                )
                .join('\r\n');
            const today = new Date().toJSON().slice(0, 10).replace(/-/g, '');
            const tableFileName = `${props.moduleName}_${today}`;
            downloadTableData(exportData, tableFileName);
        }

        function formatter(row: any, column: any) {
            if (column.property === dateKey) {
                return format(+row[dateKey], dimensionFormatType);
            }
            if (
                typeof row[column.property] === 'string' ||
                row[column.property] instanceof String
            ) {
                return row[column.property];
            }
            if (percentSourceSeries.value.indexOf(column.property) !== -1) {
                return row[column.property]
                    ? (parseFloat(row[column.property]) * 100).toFixed(2) + '%'
                    : '0%';
            }
            if (!column.property || !row[column.property]) {
                return 0;
            }
            return formatCountString(parseFloat(row[column.property]));
        }

        function sourceSeriesLinkage(source: string, serie: string) {
            if (serie === dateKey) {
                return serie;
            }
            return source.concat(seperator, serie);
        }

        function sourceSeriesName(source: string, serie: string) {
            if (serie === dateKey) {
                return serie;
            }
            const sepIndex =
                serie.indexOf(seperator) < 0
                    ? serie.length
                    : serie.indexOf(seperator);
            const seriesName = serie.substring(0, sepIndex);
            return source.concat(seperator, seriesName);
        }

        function deepFormat(mixinData: Array<Record<string, any>>) {
            return mixinData.reduce((p: Array<Record<string, any>>, v) => {
                const linkageArray = Object.keys(v)
                    .filter((key) => key !== dateKey)
                    .map((key) => {
                        const linkage = getLinkageSource(key);
                        return {
                            [linkage.linkageName]: {
                                [linkage.sourceSeriesName]: v[key],
                            },
                        };
                    });
                const fmap = linkageArray.reduce(
                    (l: Record<string, any>, c: Record<string, any>) => {
                        const tempData = Object.keys(c)
                            .map((d) => ({
                                [d]: { ...l[d], ...c[d] },
                            }))
                            .reduce((pre, cur) => ({ ...pre, ...cur }), {});
                        return { ...l, ...tempData };
                    },
                    {},
                );
                const newRows = Object.keys(fmap).map((linkageKey) => {
                    const linkProp = getLinkageProperty(linkageKey);
                    return {
                        ...fmap[linkageKey],
                        ...linkProp,
                        ...{ [dateKey]: v[dateKey] },
                    };
                });
                return [...p, ...newRows];
            }, []);
        }

        function formatTableData(result: Record<string, any>) {
            percentSourceSeries.value = Object.keys(result)
                .map((key) =>
                    result[key].series
                        .filter(
                            (series: Record<string, any>) =>
                                series.valueType === 'percent',
                        )
                        .map((series: Record<string, any>) =>
                            sourceSeriesName(key, series.seriesName),
                        ),
                )
                .reduce((p, v) => [...p, ...v]);

            const getSeriresName = (name: string, sep: string = seperator) => {
                return name.indexOf(sep) !== -1
                    ? name.substring(0, name.indexOf(sep))
                    : '';
            };

            const alignSeriesCaption = (serie: string, caption: string) => {
                const list = caption.split('-');
                list.splice(0, 1, serie);
                return list.join(seperator);
            };

            const renameLinkageToCaption = (data: Record<string, any>) => {
                Object.keys(data).forEach((key) => {
                    const series = data[key].series;
                    const seriesMap: Record<string, any> = series
                        .map((serie: Record<string, any>) => ({
                            [serie.seriesName]: alignSeriesCaption(
                                getSeriresName(serie.seriesName),
                                serie.seriesCaption,
                            ),
                        }))
                        .reduce(
                            (
                                p: Record<string, any>,
                                v: Record<string, any>,
                            ) => ({ ...p, ...v }),
                        );
                    const ret = data[key].data.map((row: Record<string, any>) =>
                        Object.keys(row).reduce(
                            (p, v) => ({
                                ...p,
                                [v === dateKey ? v : seriesMap[v]]: row[v],
                            }),
                            {},
                        ),
                    );
                    data[key].data = ret;
                });
            };
            renameLinkageToCaption(result);
            const updateSourceSeriesLinkage = (
                source: string,
                obj: Record<string, any>,
            ) => {
                return Object.keys(obj).reduce(
                    (p, v) => ({
                        ...p,
                        [sourceSeriesLinkage(source, v)]: obj[v],
                    }),
                    {},
                );
            };
            const dataArray = Object.keys(result).map((source) => {
                return result[source].data?.reduce(
                    (p: Record<string, any>, data: Record<string, any>) => ({
                        ...p,
                        [data[dateKey]]: updateSourceSeriesLinkage(
                            source,
                            data,
                        ),
                    }),
                    {},
                );
            });
            const mergedData = dataArray.reduce((a, v) => {
                return Object.keys(a)
                    .map((d) => ({
                        [d]: { ...a[d], ...v[d] },
                    }))
                    .reduce((p, c) => ({ ...p, ...c }));
            });
            const mixinData = Object.keys(mergedData)
                .sort()
                .map((t) => mergedData[t]);
            tableData.value = deepFormat(mixinData);
        }

        function updateTableData() {
            const data = cloneDeep(originData);
            tableData.value = [];
            if (!Object.keys(data).length) {
                return;
            }
            formatTableData(data);
        }

        async function getTableData() {
            try {
                isLoading.value = true;
                const linkageParams = {};
                Object.keys(props.linkage).forEach((item) => {
                    if (item !== 'dateRange') {
                        // @ts-ignore
                        linkageParams[item] = props.linkage[item].join(',');
                    }
                });
                if (!props.data?.sources || props.data?.sources.length === 0) {
                    return;
                }

                const res = {} as Record<string, any>;

                for (let i = 0; i < props.data?.sources.length; i++) {
                    const source = props.data?.sources[i];
                    const data = await getReportSeries(
                        {
                            source: source,
                            appId: props.appId,
                            dimensionType: dimensionType,
                            dateRange: props.linkage.dateRange,
                            ...linkageParams,
                        },
                        true,
                    );
                    if (data && Object.keys(data).length > 0 && source) {
                        res[source] = data;
                    }
                }
                originData = cloneDeep(res);
                updateTableData();
            } catch (e) {
                originData = {};
                tableData.value = [];
            } finally {
                isLoading.value = false;
                originTableData = tableData.value;
            }
        }

        watch(
            () => props.linkage,
            async (v) => {
                if (props.appId && v) {
                    await getTableData();
                }
            },
            { immediate: true, deep: true },
        );

        return {
            exportTableData,
            tableData,
            tableHeader,
            formatter,
            dateKey,
            isLoading,
        };
    },
});
